import React, { useEffect, useRef, useState } from 'react';
import currency from '../images/moneybag.png';
import empty from '../images/Empty.png';
import { getAuth } from 'firebase/auth';

const focusableSelector = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const getBetKey = (bet) => `${bet.id}$${bet.selectedMarket}$${bet.selectedType}`;

function BetAmountInput({ id, value, onChange, onBlur, label, error, inputRef }) {
  const [draft, setDraft] = useState(value ?? '');

  useEffect(() => {
    setDraft(value ?? '');
  }, [value]);

  const handleChange = (event) => {
    const nextValue = event.target.value.replace(',', '.');
    if (!/^\d*(?:\.\d{0,2})?$/.test(nextValue)) return;
    setDraft(nextValue);
    onChange(nextValue);
  };

  return (
    <>
      <label className="visually-hidden" htmlFor={id}>{label}</label>
      <input
        ref={inputRef}
        id={id}
        type="text"
        inputMode="decimal"
        pattern="[0-9]*[.,]?[0-9]{0,2}"
        autoComplete="off"
        spellCheck="false"
        className="input bg-darkblue inputBet"
        placeholder="0.00"
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${id}-error` : undefined}
        onChange={handleChange}
        onBlur={onBlur}
        value={draft}
      />
    </>
  );
}

function BetSlipIcon() {
  return (
    <svg width="52" height="68" viewBox="0 0 52 68" fill="none" aria-hidden="true">
      <path d="M47.9272 0.597197C49.3734 -0.454619 51.3456 0.46572 51.3456 2.17492V65.2839C51.3456 67.1246 49.2419 68.0449 47.9272 66.9931L42.931 61.0767L35.6998 66.9931C35.3054 67.2561 34.9109 67.519 34.385 67.519C33.9906 67.519 33.5962 67.2561 33.2017 66.9931L26.102 61.0767L18.8707 66.9931C18.4763 67.2561 18.0819 67.519 17.5559 67.519C17.1615 67.519 16.7671 67.2561 16.3727 66.9931L9.27289 61.0767L4.14528 66.9931C2.83051 68.0449 0.858356 67.1246 0.858356 65.2839V2.17492C0.858356 0.46572 2.83051 -0.454619 4.14528 0.597197L9.27289 6.38219L16.3727 0.597197C16.7671 0.334243 17.1615 0.0712891 17.6874 0.0712891C18.0819 0.0712891 18.4763 0.334243 18.8707 0.597197L26.102 6.38219L33.2017 0.597197C33.5962 0.334243 33.9906 0.0712891 34.5165 0.0712891C34.9109 0.0712891 35.3054 0.334243 35.6998 0.597197L42.931 6.38219L47.9272 0.597197ZM42.931 47.403V45.2994C42.931 44.7735 42.4051 44.2476 41.8792 44.2476H10.3247C9.66732 44.2476 9.27289 44.7735 9.27289 45.2994V47.403C9.27289 48.0604 9.66732 48.4549 10.3247 48.4549H41.8792C42.4051 48.4549 42.931 48.0604 42.931 47.403ZM42.931 34.7812V32.6776C42.931 32.1517 42.4051 31.6258 41.8792 31.6258H10.3247C9.66732 31.6258 9.27289 32.1517 9.27289 32.6776V34.7812C9.27289 35.4386 9.66732 35.8331 10.3247 35.8331H41.8792C42.4051 35.8331 42.931 35.4386 42.931 34.7812ZM42.931 22.1594V20.0558C42.931 19.5299 42.4051 19.004 41.8792 19.004H10.3247C9.66732 19.004 9.27289 19.5299 9.27289 20.0558V22.1594C9.27289 22.8168 9.66732 23.2113 10.3247 23.2113H41.8792C42.4051 23.2113 42.931 22.8168 42.931 22.1594Z" fill="#BBBDBF" />
    </svg>
  );
}

export default function BetsSidebar({
  selectedOdds,
  setSelectedOdds,
  closeSidebar,
  betAmounts,
  setBetAmounts,
  estimatedPayouts,
  setEstimatedPayouts,
  openLoginModal,
  handleClearAllBets,
  user,
  setWalletBalance,
  walletBalance,
  setShowAlert,
  setAlertText,
  setAnimationClass,
  setUser,
  percentOdds,
  invalidMatches,
  setInvalidMatches,
  betsOpen,
}) {
  const [stakeErrors, setStakeErrors] = useState({});
  const [submissionError, setSubmissionError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dialogRef = useRef(null);
  const headingRef = useRef(null);
  const errorSummaryRef = useRef(null);
  const inputRefs = useRef({});
  const previouslyFocusedRef = useRef(null);
  const wasOpenRef = useRef(false);

  const showTempAlert = (htmlText) => {
    setAlertText(htmlText);
    setAnimationClass('alert-fade-in');
    setShowAlert(true);
    setTimeout(() => setAnimationClass('alert-fade-out'), 2000);
    setTimeout(() => {
      setShowAlert(false);
      setAnimationClass('');
    }, 2500);
  };

  const getStakeError = (bet, value) => {
    const nowUnix = Math.floor(Date.now() / 1000);
    if (Number(bet.timestamp) <= nowUnix) return 'This match has already started.';
    if (String(value ?? '').trim() === '' || Number(value) <= 0) return 'Enter a stake greater than 0.';
    return '';
  };

  const validateAllStakes = () => {
    const nextErrors = {};
    selectedOdds.forEach((bet) => {
      const key = getBetKey(bet);
      const error = getStakeError(bet, betAmounts[key]);
      if (error) nextErrors[key] = error;
    });
    return nextErrors;
  };

  const focusFirstError = (errors) => {
    const firstKey = Object.keys(errors)[0];
    requestAnimationFrame(() => {
      if (firstKey && inputRefs.current[firstKey]) inputRefs.current[firstKey].focus();
      else errorSummaryRef.current?.focus();
    });
  };

  const applyValidationErrors = (errors) => {
    setStakeErrors(errors);
    setInvalidMatches(Object.keys(errors));
    setSubmissionError('');
    focusFirstError(errors);
  };

  useEffect(() => {
    setSubmissionError('');
  }, [betAmounts, selectedOdds, walletBalance]);

  useEffect(() => {
    const activeKeys = new Set(selectedOdds.map(getBetKey));
    setStakeErrors((currentErrors) => Object.fromEntries(
      Object.entries(currentErrors).filter(([key]) => activeKeys.has(key))
    ));
  }, [selectedOdds]);

  useEffect(() => {
    if (betsOpen) {
      previouslyFocusedRef.current = document.activeElement;
      requestAnimationFrame(() => headingRef.current?.focus());
    } else if (wasOpenRef.current) {
      setTimeout(() => {
        const mobileToggle = document.getElementById('mobile-bet-slip-toggle');
        const returnTarget = mobileToggle?.offsetParent ? mobileToggle : previouslyFocusedRef.current;
        if (returnTarget && document.contains(returnTarget)) returnTarget.focus();
      }, 0);
    }
    wasOpenRef.current = betsOpen;
  }, [betsOpen]);

  const handleDialogKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      closeSidebar();
      return;
    }
    if (event.key !== 'Tab' || !window.matchMedia('(max-width: 768px)').matches) return;

    const focusable = Array.from(dialogRef.current?.querySelectorAll(focusableSelector) || []);
    if (focusable.length === 0) {
      event.preventDefault();
      headingRef.current?.focus();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const handleBetAmountChange = (bet, value) => {
    const key = getBetKey(bet);
    setBetAmounts((currentAmounts) => ({ ...currentAmounts, [key]: value }));
    const numericValue = Number.parseFloat(value);
    const payout = Number.isFinite(numericValue) ? numericValue * Number(bet.selectedOdds) : 0;
    setEstimatedPayouts((currentPayouts) => ({ ...currentPayouts, [key]: payout.toFixed(2) }));

    if (stakeErrors[key]) {
      const error = getStakeError(bet, value);
      setStakeErrors((currentErrors) => {
        const nextErrors = { ...currentErrors };
        if (error) nextErrors[key] = error;
        else delete nextErrors[key];
        return nextErrors;
      });
      setInvalidMatches((currentInvalid) => error
        ? (currentInvalid.includes(key) ? currentInvalid : [...currentInvalid, key])
        : currentInvalid.filter((invalidKey) => invalidKey !== key));
    }
  };

  const handleStakeBlur = (bet) => {
    const key = getBetKey(bet);
    const error = getStakeError(bet, betAmounts[key]);
    setStakeErrors((currentErrors) => {
      const nextErrors = { ...currentErrors };
      if (error) nextErrors[key] = error;
      else delete nextErrors[key];
      return nextErrors;
    });
    setInvalidMatches((currentInvalid) => error
      ? (currentInvalid.includes(key) ? currentInvalid : [...currentInvalid, key])
      : currentInvalid.filter((invalidKey) => invalidKey !== key));
  };

  const removeBet = (bet) => {
    const betKey = getBetKey(bet);
    setSelectedOdds((currentOdds) => {
      const updatedOdds = currentOdds.filter((selectedBet) => getBetKey(selectedBet) !== betKey);
      if (updatedOdds.length === 0) closeSidebar();
      return updatedOdds;
    });
    setInvalidMatches((currentInvalid) => currentInvalid.filter((key) => key !== betKey));
    setStakeErrors((currentErrors) => {
      const nextErrors = { ...currentErrors };
      delete nextErrors[betKey];
      return nextErrors;
    });
    setBetAmounts((currentAmounts) => {
      const remainingAmounts = { ...currentAmounts };
      delete remainingAmounts[betKey];
      return remainingAmounts;
    });
    setEstimatedPayouts((currentPayouts) => {
      const remainingPayouts = { ...currentPayouts };
      delete remainingPayouts[betKey];
      return remainingPayouts;
    });
  };

  const saveBets = async (currentUser) => {
    setIsSubmitting(true);
    setSubmissionError('');
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch('/api/save-odds', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ selectedOdds, betAmounts, user: currentUser }),
      });
      const data = await response.json();

      if (!response.ok) {
        const message = data.error === 'Insufficient balance'
          ? 'Insufficient balance.'
          : 'Unable to place your bets. Refresh the page and try again.';
        setSubmissionError(message);
        requestAnimationFrame(() => errorSummaryRef.current?.focus());
        return;
      }

      localStorage.setItem('walletBalance', data.wallet_balance);
      setWalletBalance(data.wallet_balance);
      closeSidebar();
      handleClearAllBets();
      setStakeErrors({});
      setInvalidMatches([]);
      showTempAlert('<strong>Successfully placed bets!</strong>');
    } catch (error) {
      console.error('Error saving odds:', error);
      const message = 'Unable to place your bets. Refresh the page and try again.';
      setSubmissionError(message);
      requestAnimationFrame(() => errorSummaryRef.current?.focus());
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalEstimatedPayout = Object.values(estimatedPayouts)
    .reduce((sum, payout) => sum + (Number.parseFloat(payout) || 0), 0);
  const totalBetAmounts = Object.values(betAmounts)
    .reduce((sum, amount) => sum + (Number.parseFloat(amount) || 0), 0);
  const walletAmount = Number.parseFloat(walletBalance) || 0;
  const hasMissingStake = selectedOdds.some((bet) => {
    const value = betAmounts[getBetKey(bet)];
    return String(value ?? '').trim() === '' || Number(value) <= 0;
  });
  const hasStartedMatch = selectedOdds.some((bet) => Number(bet.timestamp) <= Math.floor(Date.now() / 1000));
  const exceedsWallet = Boolean(user) && totalBetAmounts > walletAmount;
  const placeBetUnavailable = selectedOdds.length === 0 || hasMissingStake || hasStartedMatch || exceedsWallet || isSubmitting;

  const placeBetError = exceedsWallet
    ? 'Insufficient balance.'
    : submissionError;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (selectedOdds.length === 0 || isSubmitting) return;

    const validationErrors = validateAllStakes();
    if (Object.keys(validationErrors).length > 0) {
      applyValidationErrors(validationErrors);
      return;
    }
    if (exceedsWallet) {
      setSubmissionError('Insufficient balance.');
      requestAnimationFrame(() => errorSummaryRef.current?.focus());
      return;
    }

    const currentUser = getAuth().currentUser;
    if (!currentUser) {
      setUser(null);
      openLoginModal();
      return;
    }
    setUser(currentUser);
    await saveBets(currentUser);
  };

  return (
    <div
      ref={dialogRef}
      id="bet-slip-dialog"
      className="d-flex justify-content-end bets-float shadow-left"
      role="dialog"
      aria-modal={window.matchMedia('(max-width: 768px)').matches ? 'true' : undefined}
      aria-labelledby="bet-slip-heading"
      aria-busy={isSubmitting}
      onKeyDown={handleDialogKeyDown}
    >
      <form className="bg-green bets-container" onSubmit={handleSubmit} noValidate>
        <h2 id="bet-slip-heading" ref={headingRef} className="visually-hidden" tabIndex="-1">
          My Bets, {selectedOdds.length} selection{selectedOdds.length === 1 ? '' : 's'}
        </h2>
        <div className="bg-darkgreen top-nav-bets"></div>
        <div className="bg-darkgreen shadow-box bot-nav-bets d-flex align-items-center justify-content-between">
          <div aria-hidden="true">
            <BetSlipIcon />
            My Bets <span className="badge rounded-pill bg-orange">{selectedOdds.length}</span>
          </div>
          <button type="button" className="bet-slip-icon-button" onClick={closeSidebar} aria-label="Close bet slip">
            <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
              <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708" />
            </svg>
          </button>
        </div>

        <ul className="bets-list">
          <li className="bets-clear-all text-end pt-3 pb-2 px-4">
            <button
              type="button"
              className="bet-slip-text-button text-grey font-12"
              disabled={selectedOdds.length === 0}
              onClick={() => {
                handleClearAllBets();
                setStakeErrors({});
                setInvalidMatches([]);
                closeSidebar();
              }}
            >
              Clear all
            </button>
          </li>

          {selectedOdds.length > 0 ? selectedOdds.map((bet) => {
            const betKey = getBetKey(bet);
            const error = stakeErrors[betKey];
            const isInvalid = Boolean(error) || invalidMatches.includes(betKey);
            const inputId = `stake-${betKey.replace(/[^a-zA-Z0-9_-]/g, '-')}`;
            const stakeLabel = `Stake for ${bet.selectedType} in ${bet.homeTeam} versus ${bet.awayTeam}`;

            return (
              <li key={betKey} className={`selected-bet p-3 ${isInvalid ? 'bg-red' : ''}`}>
                <button type="button" className="bet-slip-icon-button float-end" onClick={() => removeBet(bet)} aria-label={`Remove ${bet.selectedType} selection for ${bet.homeTeam} versus ${bet.awayTeam}`}>
                  <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
                    <path d="M11 1.5v1h3.5a.5.5 0 0 1 0 1h-.538l-.853 10.66A2 2 0 0 1 11.115 16h-6.23a2 2 0 0 1-1.994-1.84L2.038 3.5H1.5a.5.5 0 0 1 0-1H5v-1A1.5 1.5 0 0 1 6.5 0h3A1.5 1.5 0 0 1 11 1.5m-5 0v1h4v-1a.5.5 0 0 0-.5-.5h-3a.5.5 0 0 0-.5.5M4.5 5.029l.5 8.5a.5.5 0 1 0 .998-.06l-.5-8.5a.5.5 0 1 0-.998.06m6.53-.528a.5.5 0 0 0-.528.47l-.5 8.5a.5.5 0 0 0 .998.058l.5-8.5a.5.5 0 0 0-.47-.528M8 4.5a.5.5 0 0 0-.5.5v8.5a.5.5 0 0 0 1 0V5a.5.5 0 0 0-.5-.5" />
                  </svg>
                </button>
                <div className="matchName text-start">{bet.homeTeam} vs {bet.awayTeam}</div>
                <div className="font-12 text-lightgrey text-start">{bet.date} - {bet.time}</div>
                <div className="font-12 text-lightgrey text-start mt-2">{bet.selectedMarket}</div>
                <div className="font-15 text-start fw-bold d-flex justify-content-between">
                  <div className="text-white">{bet.selectedType}</div>
                  <div className="text-orange">{percentOdds ? `${((1 / bet.selectedOdds) * 100).toFixed(2)}%` : Number.isInteger(bet.selectedOdds * 1) ? bet.selectedOdds * 1 : (bet.selectedOdds * 1).toFixed(2)}</div>
                </div>
                <div className="d-flex justify-content-between align-items-center mt-2">
                  <div className="bet-stake-field text-start col-6">
                    <div className="d-flex bg-darkblue align-items-center justify-content-between p-1 rounded">
                      <div>
                        <BetAmountInput
                          id={inputId}
                          inputRef={(element) => { inputRefs.current[betKey] = element; }}
                          value={betAmounts[betKey] || ''}
                          label={stakeLabel}
                          error={error}
                          onChange={(value) => handleBetAmountChange(bet, value)}
                          onBlur={() => handleStakeBlur(bet)}
                        />
                      </div>
                      <div className="currency"><img src={currency} className="currencyImg" alt="" width={20} height={20} /></div>
                    </div>
                  </div>
                  <div className="text-end col-6">
                    <div className="font-12 text-lightgrey">Est. Payout</div>
                    <div className="font-15 text-white fw-bold d-flex justify-content-end align-items-center">
                      <output className="pt-1" aria-label={`Estimated payout for ${bet.selectedType}`}>{estimatedPayouts[betKey] || '0.00'}</output>
                      <img src={currency} className="currencyImg" alt="coins" width={20} height={20} />
                    </div>
                  </div>
                </div>
                {error && <div id={`${inputId}-error`} className="bet-slip-error font-12 text-start" role="alert">{error}</div>}
              </li>
            );
          }) : (
            <li className="text-center mt-5 bet-slip-empty">
              <img src={empty} className="empty" alt="" width={225} height={209} />
              <p className="text-white"><em>Empty...</em><br /><strong>Start Betting!</strong></p>
            </li>
          )}
        </ul>

        <div className="totalBets" aria-label="Bet totals">
          <div
            id="place-bet-error"
            ref={errorSummaryRef}
            className="bet-slip-error font-12 text-start px-4"
            role="alert"
            tabIndex="-1"
          >{placeBetError}</div>
          <div className="bets-total-row d-flex justify-content-between align-items-center pt-2 pb-2">
            <div className={`bets-total-values w-100 px-4 py-1 d-flex justify-content-between align-items-center ${exceedsWallet ? 'total-error' : ''}`}>
              <div className="font-15 text-white fw-bold d-flex justify-content-end align-items-center">
                <output className="pt-1" aria-label="Total stakes">{totalBetAmounts.toFixed(2)}</output>
                <img src={currency} className="currencyImg" alt="coins" width={20} height={20} />
              </div>
              <div className="font-15 text-lightgrey text-end">Total Bets</div>
            </div>
          </div>
          <div className="bets-payout-row d-flex justify-content-between align-items-center px-4">
            <div className="font-15 text-orange fw-bold d-flex justify-content-end align-items-center">
              <output className="pt-1" aria-label="Total estimated payout">{totalEstimatedPayout.toFixed(2)}</output>
              <img src={currency} className="currencyImg" alt="coins" width={20} height={20} />
            </div>
            <div className="font-15 text-orange text-end">Est. Payout</div>
          </div>
          <div className="bets-place-action text-center px-4 py-2">
            <button
              type="submit"
              className="placebet"
              disabled={selectedOdds.length === 0 || isSubmitting}
              aria-disabled={placeBetUnavailable}
              aria-describedby={placeBetError ? 'place-bet-error' : undefined}
            >
              {isSubmitting ? 'Placing bet…' : 'Place Bet'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
